<template>
  <div class="article-1">
    <div class="article__info">
      <h1 class="article__title">年輕人是大選關鍵選民？</h1>
      <h2 class="article__subtitle">民進黨未必占優勢 都市化程度決定投票率</h2>
      <div class="article__reporter">聯合報 記者：蔡晉宇、張文馨、鄭媁／報導</div>
    </div>

    <figure class="article__figure article__cover">
      <div class="article__figure-photo" :style="`background-image: url(${require('@/assets/articles/article-1-cover.jpg')})`"></div>
      <figcaption class="article__figure-caption">
        <SvgSymbol class="article__figure-caption-icon" use="#figure-caption-icon"/>
        <span>學生拿起手機，一起和蔡英文玩自拍。圖／本報資料照</span>
      </figcaption>
    </figure>

    <p class="article__paragraph">年輕族群的選票，往往是兵家必爭之地，向來被藍綠陣營視為決勝的關鍵選民。一般認為，這群人傾向支持民進黨，但根據統計結果顯示，這群人未必去投票，對投票結果缺乏實際影響力，不見得對綠營有利。</p>
    <p class="article__paragraph">這個現象帶給藍營積極開拓年輕選票的機會，因為從研究結果發現，真正左右影響這些人投票率的決定性因素是「都市化程度」。</p>
    <p class="article__paragraph">2014年的九合一地方選舉，全國22縣市中，民進黨囊括13縣市，國民黨拿下6縣市執政；由於當年最著名事件是「 <Dictionary word="318學運" meaning="318學運：又稱「太陽花學運」，從2014年3月18日至4月10日期間，由大學生發起占領國會的社會運動。"/> 」，許多分析認為這會帶動年輕人參政意願，一併改寫台灣當年政治版圖，但這群人對選舉的影響到底如何？</p>
    <p class="article__paragraph">依據內政部公布的2015年全國368鄉鎮市人口年齡結構，與中選會2016年總統大選投票結果，針對上次總統大選進行分析。選區中年輕選民所占的比率高低，對於投票率、催票率，或是藍、綠的得票率，影響都相當有限。</p>
    <p class="article__paragraph">當年的全國「 <Dictionary word="年輕指數" meaning="年輕指數：本文／本研究是以「20-39歲選民人數」為分子，「合格選民總人數」為分母，得出年輕人占總投票人數中比例的「年輕指數」。"/> 」為36.18％，代表全國選民當中，有36.18％的人口屬於20-39歲族群。</p>

    <h3 class="article__paragraph-title">年輕指數 不影響得票率高低</h3>
    <p class="article__paragraph">觀察全國368鄉鎮市區的「年輕指數」分布發現，「年輕指數」最高的高雄市那瑪夏區，以及最低的新北市平溪區，都是集中在傳統偏鄉，而不是都會區。再將各鄉鎮市區的「年輕指數」與2016年的投票紀錄交叉分析，不論對投票率、 <Dictionary word="催票率" meaning="催票率=得票數／總選舉人數<br/>得票率=得票數／投票數"/> ，或藍綠的得票率，相關性都不高。</p>

    <!-- chart-1 -->
    <div class="article__chart">
      <div class="article__chart-caption">都市化程度 vs 投票率</div>
      <div class="article__chart-figure article-1__chart-figure-1">
        <SvgSymbol use="#article-1-chart-1"/>
      </div>
      <div class="article__chart-annotation"><!--
      --><span>資料來源：內政部、中選會2016投票紀錄</span><!--
      --><span>本文自行彙整</span>
      </div>
    </div>

    <p class="article__paragraph">世新大學行政管理系教授 <Dictionary word="莊文忠" meaning="國立政治大學博士，現任世新大學行政管理系教授，台灣透明組織協會理事，以及中華組織發展協會理事。研究領域為公共政策分析、研究方法、政府績效管理、民意調查。"/> 表示，這代表總體資料中，一個區域內年輕選民比率多寡，並不會影響投票率、催票率，也無關投藍、投綠。</p>

    <h3 class="article__paragraph-title">都市化越高 年輕人越願意投票</h3>
    <p class="article__paragraph">大家覺得這樣就結束了嗎？藍綠難道就不理會年輕選票？試著持續尋找答案，要進一步爭取到年輕人的選票，各政黨必須考量到都市化程度，才能更貼近這群關鍵選民。</p>
    <p class="article__paragraph">若改以「 <Dictionary word="都市化程度高低" meaning="本文都市化程度分類標準，是根據教育部「107年兒童及少年生活狀況調查抽樣設計」的標準所訂定，依人口密度、65歲以上人口比率、專科以上教育程度人口比率、農業人口比率、每10萬人西醫人數，進行集群分析。" /> 」作為相關變數，發現都市化程度越高的鄉鎮市區，會呈現選民年齡結構越年輕、投票率越高的趨勢。</p>

    <!-- chart-2 -->
    <div class="article__chart article-1--map">
      <div class="article__chart-caption">都市化程度與年輕指數及投票率之關係</div>
      <div class="article__chart-figure article-1__chart-figure-2">
        <TownsMap class="article-1__towns-map"/>
      </div>
      <div class="article__chart-extended">
        <a class="article__chart-extended-link"
          :href="`${baseUrl}pdf/towns-level-detail.pdf`"
          target="_blank"
          title="各鄉鎮分層一覽表"><!--
        --><SvgSymbol use="#pdf"/><!--
        --><span>各鄉鎮分層一覽表</span>
        </a>
      </div>
      <div class="article__chart-annotation"><!--
      --><span>資料來源：內政部、中選會2016投票紀錄</span><!--
      --><span>本文自行彙整</span>
      </div>
    </div>

    <p class="article__paragraph">若將368鄉鎮市區分類為「高度都市化市鎮」、「中度都市化市鎮」、「新興市鎮」、「一般鄉鎮市區」、「高齡化市鎮」、「農業市鎮」、「偏遠市鎮」七個群組，都市化程度越高的群組，「年輕指數」普遍較高，代表年輕人多數住在都市。</p>

    <h3 class="article__paragraph-title">年輕人不住蛋黃區 而是蛋白區</h3>
    <p class="article__paragraph">值得注意的是，年輕指數最高的區域，並非高度都市化市鎮，而是中度都市化市鎮。莊文忠分析，高度都市化市鎮多數位處市中心的「蛋黃區」，房價較高，年輕人轉而聚集在中度都市化市鎮的「蛋白區」。</p>
    <p class="article__paragraph">莊文忠解釋，綜合以上分析數據，真正影響投票率的關鍵因素，是城市人口規模大小，而不單純是城市中的年齡結構問題。</p>

    <!-- chart-3 -->
    <div class="article__chart">
      <div class="article__chart-caption">都市化程度與投票率關係圖</div>
      <div class="article__chart-figure article-1__chart-figure-3">
        <SvgSymbol use="#article-1-chart-3"/>
      </div>
      <div class="article__chart-annotation"><!--
      --><span>資料來源：內政部、中選會2016投票紀錄</span><!--
      --><span>本文自行彙整</span>
      </div>
    </div>

    <p class="article__paragraph">再依照中選會投入研究的 <Dictionary word="抽樣設計資料" meaning="計畫名稱為「選舉人性別投票統計改良之研究」，2016年12月發表。"/> 顯示，透過都市化程度七群組分析，高度都市化市鎮在20歲到39歲的實際投票率，遠高於偏遠市鎮；同樣地，觀察各縣市實際投票率，以台北市最高，最低是金門，這兩縣市的年輕族群各年齡投票率，也顯示相同現象。</p>

    <!-- chart-4 #removed -->
    <div class="article__chart" v-if="false">
      <div class="article__chart-caption">20-39歲的各年齡層投票率</div>
      <div class="article__chart-figure article-1__chart-figure-4">
        <SvgSymbol use="#article-1-chart-4"/>
      </div>
      <div class="article__chart-annotation"><!--
      --><span>資料來源：內政部、中選會2016投票紀錄</span><!--
      --><span>本文自行彙整</span>
      </div>
    </div>

    <!-- chart-5 -->
    <div class="article__chart">
      <div class="article__chart-caption">台北市與金門縣年輕族群投票比較</div>
      <div class="article__chart-figure article-1__chart-figure-5">
        <SvgSymbol use="#article-1-chart-5"/>
      </div>
      <div class="article__chart-annotation"><!--
      --><span>資料來源：內政部、中選會2016投票紀錄</span><!--
      --><span>本文自行彙整</span>
      </div>
    </div>

    <p class="article__paragraph">催票率方面，都市化程度七群組中，2016年民進黨的催票率都比國民黨高，國民黨僅在都市地區的催票率稍為較好，卻是國民黨可努力的優勢。莊文忠分析，高度都市化地區發展較早，受國民黨影響較深，加上人口組成較穩定，藍軍催票較容易。</p>

    <!-- chart-6 -->
    <div class="article__chart">
      <div class="article__chart-caption">都市化程度與政黨催票率關係圖</div>
      <div class="article__chart-figure article-1__chart-figure-6">
        <SvgSymbol use="#article-1-chart-6"/>
      </div>
      <div class="article__chart-annotation"><!--
      --><span>資料來源：內政部、中選會2016投票紀錄</span><!--
      --><span>本文自行彙整</span>
      </div>
    </div>

    <h3 class="article__paragraph-title">青年返鄉投票 效果不彰</h3>
    <p class="article__paragraph">這些結果也符合藍綠動員的趨勢，每當投票前夕，各陣營總會號召「青年返鄉投票」，尤其是民進黨。但從研究可以看出，上次總統大選的成效不彰，青年族群的高投票率只出現在都市化地區，戶籍在偏遠鄉鎮的年輕人沒有返鄉投票。</p>
    <p class="article__paragraph">各政黨若企圖爭取更多青年族群的支持，扭轉選情，除了瞄準高度都市化的選區外，如何有效促使年輕人展開返鄉投票的具體行動，將是明年大選的藍綠重要課題。</p>

  </div>
</template>

<script>
import Dictionary from '@/components/Dictionary'
import TownsMap from '@/components/TownsMap'

export default {
  name: 'Article1',
  components: {
    Dictionary,
    TownsMap
  },
  inject: [ 'baseUrl' ]
}
</script>

<style lang="scss">
.article-1 {

  &--map {
    width: 100%;
    max-width: 1200px;
    padding: 20px 0;
    @include rwd($RWD_TABLET_SMALL) {
      padding: 40px 35px;
    }
    @include rwd($RWD_TABLET) {
      padding: 40px 50px;
    }
    @include rwd($RWD_DESKTOP) {
      padding: 60px 70px;
    }
    .article__chart-caption {
      display: none;
    }
    .article__chart-annotation {
      // margin-top: -60px;
      margin-right: 25px;
      @include rwd($RWD_TABLET_SMALL) {
        margin-right: 5px
      }
    }
  }

  &__towns-map {
    margin: 0 auto;
  }

  &__chart-figure {
    &-1 {
      @include use-fixed-ratio(543, 245);
    }

    &-3 {
      @include use-fixed-ratio(672, 455);
    }

    &-4 {
      @include use-fixed-ratio(726, 312);
    }

    &-5 {
      @include use-fixed-ratio(777, 449);
    }

    &-6 {
      @include use-fixed-ratio(799, 505);
    }

    .svg-symble {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
    }
  }
}
</style>
